/**
 * Componente funcional para renderizar un autocompletado personalizado.
 * @module CustomAutocomplete
 * @param {object} props - Propiedades del componente.
 * @param {string} props.label - Etiqueta del autocompletado.
 * @param {array} props.options - Opciones disponibles para el autocompletado.
 * @param {number} props.grid - Tamaño del grid del componente.
 * @param {number} props.gridXS - Tamaño del grid XS del componente.
 * @param {function} props.onTextChange - Función manejadora del cambio de texto en el campo de entrada.
 * @param {function} props.onChange - Función manejadora del cambio de valor seleccionado.
 * @param {function} props.getOptionLabel - Función para obtener la etiqueta de la opción seleccionada.
 * @param {function} props.renderOption - Función para renderizar una opción en el autocompletado.
 * @param {boolean} [props.multiple] - Indica si se permite la selección múltiple.
 * @param {string} [props.width] - Ancho del autocompletado.
 * @param {string} [props.height] - Alto del autocompletado.
 * @param {boolean} [props.required] - Indica si el autocompletado es obligatorio.
 * @param {boolean} [props.helperText] - Indica si el autocompletado contiene un texto de ayuda.
 * @param {boolean} [props.disabled] - Indica si el autocompletado es deshabilitado.
 * @param {boolean} [props.multiple] - Indica si el autocompletado es multiple.
 * @returns {JSX.Element} Elemento de React que representa el autocompletado personalizado.
 */
import React from "react";
import { CircularProgress, Grid, TextField, useTheme } from "@mui/material";
import { CssAutocomplete } from "../CSS/CssAutocomplete";

const CustomAutocomplete = ({
  label,
  defaultValue,
  value,
  options,
  onTextChange,
  onChange,
  getOptionLabel,
  renderOption,
  width,
  required,
  groupBy,
  renderGroup,
  filterOptions,
  helperText,
  disabled,
  multiple = false, // Prop para seleccionar una o múltiples opciones
  noOptionsText,
  onKeyDown,
  color,
  InputProps,
  loading,
  onOpen,
  onClose,
  gridXS,
  gridMD,
  grid,
  renderValue,
  isOptionEqualToValue,
  align,
}) => {
  /**
   * Hook que obtiene el tema actual utilizando `useTheme` de Material-UI.
   * @type {Theme} El tema actual utilizado en la aplicación, que incluye configuraciones de color, tipografía, etc.
   */
  const theme = useTheme();

  return (
    <Grid
      size={{ xs: gridXS, md: gridMD, xl: grid, lg: grid }}
      sx={
        align && {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: 2,
        }
      }
    >
      <CssAutocomplete
        style={{
          width: width ? width : "96%",
          color: color ? color : theme.palette.mode,
        }}
        multiple={multiple}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        disablePortal
        id={label}
        options={options}
        filterOptions={filterOptions}
        groupBy={groupBy}
        getOptionLabel={getOptionLabel || ""}
        renderOption={renderOption}
        disabled={disabled}
        loading={loading}
        onOpen={onOpen}
        onClose={onClose}
        renderInput={(params) => (
          <TextField
            required={required}
            onChange={onTextChange}
            {...params}
            label={label}
            helperText={helperText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : InputProps ? (
                    InputProps
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
        renderValue={renderValue}
        renderGroup={renderGroup}
        noOptionsText={noOptionsText}
        onKeyDown={onKeyDown}
        isOptionEqualToValue={
          isOptionEqualToValue
            ? isOptionEqualToValue
            : (option, value) => option?.label === value?.label
        }
      />
    </Grid>
  );
};
export default CustomAutocomplete;
